import { useEffect, useState } from "react";
import api from "@/lib/api";
import Pagination from "@/components/admin/Pagination";
import ExportCSVButton from "@/components/admin/ExportCSVButton";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Audit log — read-only stream of admin write events.
 * Backend: GET /api/admin/audit-log (paginated, server-driven).
 */

function ActorBadge({ role }) {
  const cls =
    role === "owner"
      ? "bg-brand-navy text-white"
      : role === "admin"
      ? "bg-amber-100 text-amber-900 border border-amber-200"
      : "bg-gray-100 text-gray-700";
  return <span className={`px-2 py-0.5 text-xs ${cls}`}>{role || "—"}</span>;
}

function fmtTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  // Display in viewer's local time. Tooltip shows raw UTC.
  return d.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "medium" });
}

export default function AdminAuditLog() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ actor: "", action: "", target_type: "" });
  const [expandedId, setExpandedId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (filters.actor) params.set("actor", filters.actor);
    if (filters.action) params.set("action", filters.action);
    if (filters.target_type) params.set("target_type", filters.target_type);
    api
      .get(`/admin/audit-log?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message || "Failed to load"));
  }, [page, pageSize, filters]);

  return (
    <div data-testid="admin-audit-log">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Audit log · every admin write</div>
          <div className="text-xs text-brand-muted mt-1">
            Read-only stream. Retention: forever. Reads are not logged — only writes.
          </div>
        </div>
        {isOwner && (
          <ExportCSVButton
            path="/admin/audit-log/export.csv"
            params={{ action: filters.action || undefined, actor_id: filters.actor || undefined }}
            filename="colourmatch-audit-log.csv"
            testId="audit-log-export-csv"
            label="Export CSV (Owner)"
          />
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <input
          data-testid="audit-filter-actor"
          placeholder="Actor email (exact)"
          value={filters.actor}
          onChange={(e) => { setFilters({ ...filters, actor: e.target.value }); setPage(1); }}
          className="border border-brand-linen bg-white px-3 py-2 text-sm"
        />
        <input
          data-testid="audit-filter-action"
          placeholder="Action (e.g. admin.login)"
          value={filters.action}
          onChange={(e) => { setFilters({ ...filters, action: e.target.value }); setPage(1); }}
          className="border border-brand-linen bg-white px-3 py-2 text-sm"
        />
        <input
          data-testid="audit-filter-target-type"
          placeholder="Target type (e.g. session)"
          value={filters.target_type}
          onChange={(e) => { setFilters({ ...filters, target_type: e.target.value }); setPage(1); }}
          className="border border-brand-linen bg-white px-3 py-2 text-sm"
        />
      </div>

      {err && (
        <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 mb-4">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-brand-linen overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-linen/30">
            <tr className="text-left text-xs uppercase tracking-wider text-brand-muted">
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Details</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-brand-muted">
                  No audit entries match these filters yet.
                </td>
              </tr>
            )}
            {data.items.map((it) => (
              <tr
                key={it.id}
                data-testid={`audit-row-${it.id}`}
                className="border-t border-brand-linen/50"
              >
                <td className="px-4 py-3 whitespace-nowrap" title={it.ts}>
                  {fmtTs(it.ts)}
                </td>
                <td className="px-4 py-3 truncate max-w-[200px]" title={it.actor_email}>
                  {it.actor_email || "—"}
                </td>
                <td className="px-4 py-3">
                  <ActorBadge role={it.actor_role} />
                </td>
                <td className="px-4 py-3 font-mono text-xs">{it.action}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {it.target_type}
                  {it.target_id ? ` · ${it.target_id.slice(0, 8)}…` : ""}
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`audit-row-toggle-${it.id}`}
                    onClick={() => setExpandedId(expandedId === it.id ? null : it.id)}
                    className="text-xs text-brand-teal hover:underline"
                  >
                    {expandedId === it.id ? "Hide" : "View"}
                  </button>
                  {expandedId === it.id && (
                    <pre data-testid={`audit-row-detail-${it.id}`} className="mt-2 text-xs bg-brand-linen/30 p-3 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(
  { before: it.before, after: it.after, ip: it.ip, user_agent: it.user_agent },
  null, 2
)}
                    </pre>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={data.total}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
      />
    </div>
  );
}
